import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ApiCustomer } from "src/api/api_customer";
import { toast } from "react-toastify";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "src/components/base/table";
import TablePagination from "src/components/table-pagination";
import { ChevronDownIcon, ClipboardDocumentIcon } from "@heroicons/react/20/solid";
import SETTINGS_CONTEXT from "src/context/customer/settings";
import { Strong, Text } from "src/components/base/text";
import React, { useEffect } from "react";
import { Badge } from "src/components/base/badges/base";
import { Dropdown, DropdownButton, DropdownItem, DropdownLabel, DropdownMenu } from "src/components/base/dropdown";
import { Field, FieldGroup, Fieldset, Label } from "src/components/base/fieldset";
import { Input } from "src/components/base/input";
import Form from "src/components/base/form";
import { useForm } from "@tanstack/react-form";
import WS from "src/api/ws";
import { TagBadge } from "src/components/base/badges/tag";

/**
 * The properties for {@link PendingExitNodeOverview}
 */
export type PendingExitNodeOverviewProps = {};

const LIMIT = 25;

/**
 * Overview for all pending exit nodes
 */
export default function PendingExitNodeOverview(props: PendingExitNodeOverviewProps) {
    const [t] = useTranslation("customer-exit-nodes");
    const [tg] = useTranslation();
    const navigate = useNavigate();

    const { page, search } = Route.useSearch();
    const router = useRouter();

    const settingsContext = React.useContext(SETTINGS_CONTEXT);

    const data = Route.useLoaderData();
    if (!data) {
        return;
    }

    const form = useForm({
        defaultValues: {
            search: search,
        },
    });

    useEffect(() => {
        const listener = WS.addEventListener("message.ExitNodesChanged", () => {
            router.invalidate({ sync: true });
        });

        return () => WS.removeEventListener(listener);
    }, []);

    return (
        <div className={"flex flex-col gap-6"}>
            <Form className={"grid grid-cols-2 gap-12"} onSubmit={form.handleSubmit}>
                <Fieldset>
                    <FieldGroup>
                        <form.Field
                            name={"search"}
                            validators={{
                                onChangeAsync: async ({ fieldApi }) => {
                                    await navigate({
                                        to: "/c/exitnodes/pending",
                                        search: {
                                            page: 1,
                                            search: fieldApi.state.value,
                                        },
                                    });
                                },
                                onChangeAsyncDebounceMs: 500,
                            }}
                        >
                            {(fieldApi) => (
                                <Field>
                                    <Label>{t("label.filter")}</Label>
                                    <Input
                                        value={fieldApi.state.value}
                                        onChange={(e) => fieldApi.handleChange(e.target.value)}
                                    />
                                </Field>
                            )}
                        </form.Field>
                    </FieldGroup>
                </Fieldset>
            </Form>
            <Table dense={true}>
                <TableHead>
                    <TableRow>
                        <TableHeader>{t("label.device-name")}</TableHeader>
                        <TableHeader>{t("label.last-seen")}</TableHeader>
                        <TableHeader>{t("label.addresses")}</TableHeader>
                        <TableHeader className={"max-lg:hidden"}>{t("label.first-login")}</TableHeader>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data.items.map(({ device, uuid, advertise }) => (
                        <TableRow href={"/c/devices/$deviceId/general"} params={{ deviceId: device.uuid }} key={uuid}>
                            <TableCell>
                                <div className={"flex flex-col gap-1"}>
                                    <div className={"flex gap-2"}>
                                        <Strong>{device.dnsname}</Strong>
                                        {!advertise && <Badge color={"teal"}>{tg("label.disabled")}</Badge>}
                                    </div>
                                    <div className={"flex flex-wrap gap-1"}>
                                        {device.tags?.map((tag) => (
                                            <TagBadge key={tag.uuid} tag={tag} />
                                        ))}
                                    </div>
                                </div>
                            </TableCell>
                            <TableCell>
                                {device.online ? (
                                    <div className={"flex items-center gap-2"}>
                                        <div className={"size-2 animate-pulse rounded-sm bg-green-500"} />
                                        <Text className={"max-lg:hidden"}>{t("label.online")}</Text>
                                    </div>
                                ) : device.last_seen ? (
                                    new Date(device.last_seen).toLocaleString("de-de")
                                ) : (
                                    "?"
                                )}
                            </TableCell>
                            <TableCell>
                                <div className={"flex items-center gap-1"}>
                                    <span>{device.ipv4}</span>
                                    <Dropdown>
                                        <DropdownButton plain={true}>
                                            <ChevronDownIcon />
                                        </DropdownButton>
                                        <DropdownMenu anchor={"bottom end"}>
                                            {[
                                                device.ipv4,
                                                device.ipv6,
                                                device.dnsname,
                                                ...settingsContext.settings.headscale_domains.map(
                                                    (domain) => `${device.dnsname}.${domain}`,
                                                ),
                                            ].map(
                                                (address) =>
                                                    address && (
                                                        <DropdownItem
                                                            key={address}
                                                            onClick={async () => {
                                                                await navigator.clipboard.writeText(address);
                                                                toast.success(tg("toast.copied-to-clipboard"));
                                                            }}
                                                        >
                                                            <ClipboardDocumentIcon />
                                                            <DropdownLabel>{address}</DropdownLabel>
                                                        </DropdownItem>
                                                    ),
                                            )}
                                        </DropdownMenu>
                                    </Dropdown>
                                </div>
                            </TableCell>
                            <TableCell className={"max-lg:hidden"}>
                                {new Date(device.created_at).toLocaleDateString("de-de")}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            <TablePagination
                currentPage={page}
                maxPages={data.total == 0 ? 1 : Math.ceil(data.total / LIMIT)}
                href={"/c/exitnodes/pending"}
                getSearchParams={(newPage: number) => ({ page: newPage })}
            />
        </div>
    );
}

/**
 * Type that defines the search parameter of this endpoint
 */
type PendingExitNodesOverviewSearch = {
    /** The current page number we're on */
    page: number;
    /** The search term that should be used */
    search: string;
};

export const Route = createFileRoute("/_customer/c/exitnodes/_exitnodes/pending")({
    component: PendingExitNodeOverview,
    validateSearch: (search: Record<string, unknown>): PendingExitNodesOverviewSearch => {
        const page = Number(search?.page ?? 1);

        return {
            page: page <= 0 ? 1 : page,
            search: (search.search as string) || "",
        };
    },
    loaderDeps: ({ search: { page, search } }) => ({ page, search }),
    loader: async ({ deps }) =>
        await ApiCustomer.exitNode.getPending({
            limit: LIMIT,
            offset: (deps.page - 1) * LIMIT,
            filter_name: deps.search,
        }),
});
